import { mapValues, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { sortAnalysisHistoryDescending } from '@paragon/client-tools';
import { AnalysisHistoryRecord } from '@paragon/contract-library';

import { RootState } from '../store';
import { getComponentDesignGuidsByProjectGuid } from './getComponentDesignGuidsByProjectGuid';
import { getDoWorkingChangesExistByLoadedComponentDesignGuid } from './getDoWorkingChangesExistByComponentDesignGuid';
import { getProductionComponentInstancesByProductionGroupGuid } from './getProductionComponentInstancesByProductionGroupGuid';
import { getLoadedComponentDesignsWithInformation } from './trusses';

export const getAnalysisSetGuidsByProductionGroupGuid = createSelector(
  getProductionComponentInstancesByProductionGroupGuid,

  (productionComponentInstancesByProductionGroupGuid): { readonly [productionGroupGuid: string]: readonly string[] } =>
    mapValues(productionComponentInstancesByProductionGroupGuid, (productionComponentInstancesInProductionGroup) =>
      uniq(productionComponentInstancesInProductionGroup.map((instance) => instance.analysisSetGuid)),
    ),
);

export const getAnalysisSetGuidIsMostRecentMap = createSelector(
  getLoadedComponentDesignsWithInformation,

  (loadedComponentDesignsWithInformation): { readonly [analysisSetGuid: string]: boolean } =>
    Object.fromEntries(
      Object.values(loadedComponentDesignsWithInformation).flatMap((loadedComponentDesignWithInformation) => {
        const { history } = loadedComponentDesignWithInformation;
        const orderedHistory = sortAnalysisHistoryDescending(history);
        const [mostRecentAnalysisRecord, ...olderAnalysisRecords] = orderedHistory;
        return [
          [mostRecentAnalysisRecord.analysisSetGuid, true] as const,
          ...olderAnalysisRecords.map((analysisRecord) => [analysisRecord.analysisSetGuid, false] as const),
        ];
      }),
    ),
);

export const getAnalysisHistoryRecordByAnalysisSetGuid = createSelector(
  getLoadedComponentDesignsWithInformation,

  (loadedComponentDesignsWithInformation): { readonly [analysisSetGuid: string]: AnalysisHistoryRecord } =>
    Object.fromEntries(
      Object.values(loadedComponentDesignsWithInformation)
        .flatMap((loadedComponentDesignsWithInformation) => loadedComponentDesignsWithInformation.history)
        .map((analysisHistoryRecord) => [analysisHistoryRecord.analysisSetGuid, analysisHistoryRecord] as const),
    ),
);

export const getAnalysisSetGuidsNotInProjectOrAtDifferentVersionByProductionGroupGuid = createSelector(
  (state: RootState) => state.productionGroups,
  getAnalysisSetGuidsByProductionGroupGuid,
  getAnalysisHistoryRecordByAnalysisSetGuid,
  getComponentDesignGuidsByProjectGuid,
  getAnalysisSetGuidIsMostRecentMap,
  getDoWorkingChangesExistByLoadedComponentDesignGuid,

  (
    productionGroups,
    analysisSetGuidsByProductionGroupGuid,
    analysisHistoryRecordByAnalysisSetGuid,
    componentDesignGuidsByProjectGuid,
    analysisSetGuidIsMostRecentMap,
    workingChangesExistByLoadedComponentDesignGuid,
  ): { readonly [productionGroupGuid: string]: readonly string[] } =>
    mapValues(analysisSetGuidsByProductionGroupGuid, (analysisSetGuidsForProductionGroup, productionGroupGuid) =>
      analysisSetGuidsForProductionGroup.filter((analysisSetGuid) => {
        const { projectGuid } = productionGroups[productionGroupGuid];
        const analysisHistoryRecordForAnalysisSetGuid = analysisHistoryRecordByAnalysisSetGuid[analysisSetGuid];
        if (analysisHistoryRecordForAnalysisSetGuid == null) return false;
        const { componentDesignGuid } = analysisHistoryRecordForAnalysisSetGuid;
        const componentDesignsForActiveProject = componentDesignGuidsByProjectGuid[projectGuid];
        const componentExistsOutsideActiveProject = componentDesignsForActiveProject
          ? !componentDesignsForActiveProject.includes(componentDesignGuid)
          : false;
        const analysisSetGuidIsNotMostRecent = !analysisSetGuidIsMostRecentMap[analysisSetGuid];
        const workingChangesExist = workingChangesExistByLoadedComponentDesignGuid[componentDesignGuid];
        return componentExistsOutsideActiveProject || analysisSetGuidIsNotMostRecent || workingChangesExist;
      }),
    ),
);

export const getAnalysisSetGuidsNotInProjectOrAtDifferentVersionForActiveProductionGroupBatchesAndBundlesTab =
  createSelector(
    getAnalysisSetGuidsNotInProjectOrAtDifferentVersionByProductionGroupGuid,
    (state: RootState) => state.batchesAndBundlesTab.activeProductionGroupGuid,

    (analysisSetGuidsNotInProjectOrAtDifferentVersionByProductionGroup, activeProductionGroupGuid) =>
      activeProductionGroupGuid === null
        ? []
        : analysisSetGuidsNotInProjectOrAtDifferentVersionByProductionGroup[activeProductionGroupGuid],
  );
